import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import { useStoreBase } from "@/lib/store";
import { listingImageUrl } from "@/lib/productGallery";
import { money } from "@/lib/money";
import { Stars } from "./ProductCard";

export default function StorefrontAuthor({ config }) {
  const { slug, base } = useStoreBase();
  const { authorSlug } = useParams();
  const [page, setPage] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (!config?.author_pages_active || !slug || !authorSlug) return;
    setMissing(false);
    setPage(null);
    api.get(`/store/${slug}/author/${authorSlug}`).then(({ data }) => setPage(data)).catch(() => setMissing(true));
  }, [config?.author_pages_active, slug, authorSlug]);

  if (!config?.author_pages_active || missing) return <Navigate to={`${base}/`} replace />;
  if (!page) return <div className="max-w-6xl mx-auto px-4 py-16 text-stone-500">Loading author…</div>;

  const author = page.author || {};
  const products = page.products || [];

  return (
    <div className="bg-white" data-testid="author-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row gap-5 sm:gap-8 items-start">
          <div className="w-36 h-44 sm:w-44 sm:h-56 rounded-2xl overflow-hidden bg-stone-100 shrink-0 shadow-sm">
            {author.photo_url ? (
              <img src={mediaUrl(author.photo_url)} alt={author.name} className="w-full h-full object-cover" data-testid="author-photo" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-stone-300">{(author.name || "?").slice(0, 1)}</div>
            )}
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-stone-900" data-testid="author-name">{author.name}</h1>
            {author.description ? <p className="mt-3 text-sm sm:text-base text-stone-600 leading-7 whitespace-pre-wrap max-w-3xl">{author.description}</p> : null}
            {author.email ? <a href={`mailto:${author.email}`} className="mt-3 inline-block text-sm text-stone-700 hover:underline" data-testid="author-email">{author.email}</a> : null}
          </div>
        </div>

        <h2 className="mt-10 sm:mt-14 pt-8 border-t border-stone-200 text-xl sm:text-2xl font-bold text-stone-900" data-testid="author-books-heading">Books By {author.name}</h2>
        {products.length === 0 ? (
          <p className="mt-6 text-sm text-stone-500">No products for this author yet.</p>
        ) : (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {products.map((p) => {
              const cover = listingImageUrl(p.images);
              const sale = p.mrp && Number(p.mrp) > Number(p.price);
              return (
                <Link key={p.id} to={`${base}/product/${p.slug}`} className="group min-w-0" data-testid={`author-book-${p.slug}`}>
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-stone-100">
                    {cover ? <img src={mediaUrl(cover)} alt={p.name} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" /> : <div className="w-full h-full flex items-center justify-center text-stone-300 text-3xl">{(p.name || "?").slice(0, 1)}</div>}
                    {sale ? <span className="absolute top-2 left-2 w-11 h-11 rounded-full bg-amber-400 text-[10px] font-extrabold text-stone-900 flex items-center justify-center leading-none">SALE!</span> : null}
                  </div>
                  <div className="mt-2 text-sm font-semibold text-stone-900 line-clamp-2">{p.name}</div>
                  <div className="mt-1 flex items-center gap-1 text-stone-400">
                    <Stars value={Number(p.rating_avg || 0)} size="sm" />
                    <span className="text-[11px]">{Number(p.rating_count || 0)}</span>
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5 line-clamp-1">{author.name}</div>
                  <div className="mt-1 flex items-baseline gap-2 font-mono-nums">
                    <span className={`font-bold ${sale ? "text-rose-600" : "text-stone-900"}`}>{money(p.price, p.currency_symbol || "₹")}</span>
                    {sale ? <span className="text-xs text-stone-400 line-through">{money(p.mrp, p.currency_symbol || "₹")}</span> : null}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
